import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Leaf, ShieldCheck, ScanLine } from "lucide-react";
import CloudsBackground from "@/components/layout/CloudsBackground";

export default function LoginPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("credentials"); // credentials | 2fa_setup | 2fa_verify
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeToken, setChallengeToken] = useState("");
  const [qr, setQr] = useState(null);
  const [secret, setSecret] = useState(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email: email.trim().toLowerCase(), password });
      setChallengeToken(data.challenge_token);
      if (data.status === "2fa_setup_required") {
        const init = await api.post("/auth/2fa/setup-init", { challenge_token: data.challenge_token });
        setQr(init.data.qr_data_url);
        setSecret(init.data.secret);
        setStep("2fa_setup");
      } else {
        setStep("2fa_verify");
      }
    } catch (e) {
      setError(fmtError(e));
    } finally { setLoading(false); }
  };

  const submitSetup = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/2fa/setup-confirm", { challenge_token: challengeToken, code });
      toast.success("2FA enabled — welcome");
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setError(fmtError(e));
    } finally { setLoading(false); }
  };

  const submitVerify = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/2fa/verify", { challenge_token: challengeToken, code });
      await refresh();
      nav("/dashboard");
    } catch (e) {
      setError(fmtError(e));
    } finally { setLoading(false); }
  };

  return (
    <>
      <CloudsBackground />
      <div className="wv-login">
      <div className="wv-login-left">
        <div className="w-full max-w-md wv-card p-10">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-white/70 border border-white/60 flex items-center justify-center shadow-[0_1px_0_rgba(255,255,255,0.9)_inset,0_6px_14px_-8px_rgba(28,40,60,0.15)]">
              <Leaf size={20} strokeWidth={1.5} className="text-[var(--wv-primary)]" />
            </div>
            <div>
              <div className="wv-overline">Secure area</div>
              <div className="text-lg font-medium" style={{ fontFamily: "Outfit" }}>Sign in to continue</div>
            </div>
          </div>

          {step === "credentials" && (
            <form onSubmit={submitCredentials} className="space-y-5" data-testid="login-form">
              <div>
                <h2 className="text-3xl font-light tracking-tight" style={{ fontFamily: "Outfit" }}>Sign in</h2>
                <p className="text-sm text-[var(--wv-text-2)] mt-1">Two-factor authentication is required.</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--wv-text-2)]">Email</label>
                <input
                  data-testid="login-email"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-11 px-4 wv-input"
                  placeholder="you@company.com"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[var(--wv-text-2)]">Password</label>
                <input
                  data-testid="login-password"
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-11 px-4 wv-input"
                  placeholder="••••••••"
                />
              </div>
              {error && <div className="text-sm text-[var(--wv-error)]" data-testid="login-error">{error}</div>}
              <button
                data-testid="login-submit"
                type="submit" disabled={loading}
                className="w-full h-11 wv-btn-primary font-medium disabled:opacity-60"
              >
                {loading ? "Signing in..." : "Continue"}
              </button>
            </form>
          )}

          {step === "2fa_setup" && (
            <form onSubmit={submitSetup} className="space-y-5" data-testid="totp-setup-form">
              <div>
                <div className="flex items-center gap-2 wv-overline">
                  <ScanLine size={14} /> First-time setup
                </div>
                <h2 className="text-3xl font-light tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>Enroll 2FA</h2>
                <p className="text-sm text-[var(--wv-text-2)] mt-1">
                  Scan the QR with Google Authenticator or Authy, then enter the 6-digit code.
                </p>
              </div>
              {qr && (
                <div className="p-5 wv-card flex flex-col items-center gap-3">
                  <img src={qr} alt="TOTP QR" className="w-44 h-44" data-testid="totp-qr" />
                  <div className="text-xs text-[var(--wv-text-3)] font-mono break-all text-center">{secret}</div>
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm text-[var(--wv-text-2)]">6-digit code</label>
                <input
                  data-testid="totp-setup-code"
                  inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full h-11 px-4 rounded-sm bg-white border border-[var(--wv-border)] font-mono tracking-widest text-center text-lg"
                  placeholder="123456"
                />
              </div>
              {error && <div className="text-sm text-[var(--wv-error)]" data-testid="totp-error">{error}</div>}
              <button
                data-testid="totp-setup-confirm"
                type="submit" disabled={loading || code.length !== 6}
                className="w-full h-11 wv-btn-primary font-medium disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Enable 2FA & Sign in"}
              </button>
            </form>
          )}

          {step === "2fa_verify" && (
            <form onSubmit={submitVerify} className="space-y-5" data-testid="totp-verify-form">
              <div>
                <div className="flex items-center gap-2 wv-overline"><ShieldCheck size={14}/> Two-factor</div>
                <h2 className="text-3xl font-light tracking-tight mt-1" style={{ fontFamily: "Outfit" }}>Enter 2FA code</h2>
                <p className="text-sm text-[var(--wv-text-2)] mt-1">From your authenticator app.</p>
              </div>
              <input
                data-testid="totp-verify-code"
                inputMode="numeric" pattern="[0-9]*" maxLength={6} required
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="w-full h-11 px-4 wv-input font-mono tracking-widest text-center text-lg"
                placeholder="123456"
              />
              {error && <div className="text-sm text-[var(--wv-error)]">{error}</div>}
              <button
                data-testid="totp-verify-submit"
                type="submit" disabled={loading || code.length !== 6}
                className="w-full h-11 wv-btn-primary font-medium disabled:opacity-60"
              >
                {loading ? "Verifying..." : "Verify & Sign in"}
              </button>
            </form>
          )}
        </div>
      </div>
      <div className="wv-login-right">
        <div className="wv-orb wv-orb-1" />
        <div className="wv-orb wv-orb-2" />
        <div className="wv-orb wv-orb-3" />
      </div>
      </div>
    </>
  );
}

